import React, { useEffect } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useFetch } from '../../../hooks/useFetch';
import {
  Box,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Link as ChakraLink,
  SimpleGrid,
  Spinner,
  Text,
  useBreakpointValue,
  FlexProps,
} from '@chakra-ui/react';
import { SectionHeading } from '../../../components/SectionHeading';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import { useBranchDetailForm } from '../hooks/useBranchDetailForm';
import { Branch } from '../../../domain/branch';
import { FaCheck } from 'react-icons/fa';
import { stateToString } from '../../../domain/AddressState';
import { PaymentPlan, planToString } from '../../../domain/payment-plan';

interface GroupedPaymentPlans {
  debit: PaymentPlan[];
  debitInternational: PaymentPlan[];
  credit: PaymentPlan[];
  creditInternational: PaymentPlan[];
  prepaid: PaymentPlan[];
  prepaidInternational: PaymentPlan[];
  creditSingleInstallment: PaymentPlan[];
  creditMultipleInstallments: PaymentPlan[];
  creditSingleInstallmentInternational: PaymentPlan[];
  creditMultipleInstallmentsInternational: PaymentPlan[];
}

interface BranchResponse extends Branch {
  branch_payment_plans: {
    payment_plan: PaymentPlan;
  }[];
}

export const BranchDetail = () => {
  const params = useParams();
  const { data, error, loading } = useFetch<BranchResponse>(`/merchants-view/branches/${params.id}`);
  const { controls, reset } = useBranchDetailForm();
  const columns = useBreakpointValue({ 'md': 2, 'lg': 3 });

  useEffect(() => {
    if (data) {
      reset({ ...data, address_state: stateToString(data.address_state) });
    }
  }, [data]);

  const groupPaymentPlans = (paymentPlans: PaymentPlan[]): GroupedPaymentPlans => {
    return paymentPlans.reduce(
      (acc: GroupedPaymentPlans, plan) => {
        if (plan.payment_method === 'DEBIT') {
          if (plan.is_international) acc.debitInternational.push(plan);
          else acc.debit.push(plan);
        } else if (plan.payment_method === 'CREDIT') {
          if (plan.is_international) {
            if (plan.has_installments) acc.creditMultipleInstallmentsInternational.push(plan);
            else acc.creditSingleInstallmentInternational.push(plan);
          } else {
            if (plan.has_installments) acc.creditMultipleInstallments.push(plan);
            else acc.creditSingleInstallment.push(plan);
          }
        } else if (plan.payment_method === 'PREPAID') {
          if (plan.is_international) acc.prepaidInternational.push(plan);
          else acc.prepaid.push(plan);
        }
        return acc;
      },
      {
        debit: [],
        debitInternational: [],
        credit: [],
        creditInternational: [],
        prepaid: [],
        prepaidInternational: [],
        creditSingleInstallment: [],
        creditMultipleInstallments: [],
        creditSingleInstallmentInternational: [],
        creditMultipleInstallmentsInternational: [],
      } as GroupedPaymentPlans
    );
  };

  return (
    <>
      <ChakraLink as={RouterLink} to="../." color="neutral.800">
        <Flex alignItems="center">
          <MdKeyboardArrowLeft />
          <Text marginLeft="l">Volver a Sucursales</Text>
        </Flex>
      </ChakraLink>

      {loading && <Spinner />}
      <Box maxWidth="100%" maxHeight="70vh" overflowY="auto">
        <Center>
        <Box>
          {error && <Text marginTop="xxl" width="full" textAlign="center" color="neutral.800">Error al cargar los datos solicitados.</Text>}

          {!error &&
            <form>
              <SectionHeading label="Datos Generales" />

              <SimpleGrid columns={columns} spacing="xl" paddingBottom="xl" marginBottom="xl">
                <FormControl>
                  <FormLabel>ID Sucursal</FormLabel>
                  <Input type="text" {...controls.identifier} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Nombre de comercio</FormLabel>
                  <Input type="text" {...controls.business_name} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Nombre de sucursal</FormLabel>
                  <Input type="text" {...controls.fantasy_name} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Información adicional</FormLabel>
                  <Input type="text" {...controls.description} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Estado</FormLabel>
                  <Input type="text" {...controls.address_state} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Ciudad</FormLabel>
                  <Input type="text" {...controls.address_city} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Calle</FormLabel>
                  <Input type="text" {...controls.address_street} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Número</FormLabel>
                  <Input type="text" {...controls.address_number} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Departamento</FormLabel>
                  <Input type="text" {...controls.address_apartment} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Piso</FormLabel>
                  <Input type="text" {...controls.address_floor} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Código postal</FormLabel>
                  <Input type="text" {...controls.address_zip} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Teléfono</FormLabel>
                  <Input type="text" {...controls.phone} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input type="text" {...controls.email} disabled />
                </FormControl>

              </SimpleGrid>

              <SectionHeading label="Cuenta bancaria" />
              <SimpleGrid columns={columns} spacing="xl" paddingBottom="xl" marginBottom="xl">
                <FormControl>
                  <FormLabel>Alias para CLABE</FormLabel>
                  <Input type="text" {...controls.account_alias} disabled />
                </FormControl>
                <FormControl>
                  <FormLabel>#CLABE</FormLabel>
                  <Input type="text" {...controls.cbu_or_cvu} disabled />
                </FormControl>
              </SimpleGrid>

              <SectionHeading label="Condiciones comerciales" />
              <Box width="100%" marginTop="xl" border="1px" borderColor="neutral.200" borderRadius="md">
                <Flex
                  marginX="xl"
                  wrap="wrap"
                  justify="flex-start"
                  align="flex-start"
                  direction="column"
                >
                  {data && (
                    <PaymentPlanDescriptor
                      paymentPlans={groupPaymentPlans(data.payment_plans)}
                      marginBottom="xl"
                    />
                  )}
                </Flex>
              </Box>
            </form>
          }
        </Box>
        </Center>
      </Box>
    </>
  );
};

interface PaymentPlanProps extends FlexProps {
  paymentPlans: GroupedPaymentPlans;
}

const PaymentPlanDescriptor = ({ paymentPlans, ...props }: PaymentPlanProps) => {
  return (
    <Flex flexDirection="column" width="100%" {...props}>
      <Text marginY="xl" fontSize="xl" marginLeft="l">Débito</Text>
      {paymentPlans.debit.length > 0 && (
        <Box marginBottom="xl" width="100%">
          <Flex wrap="wrap" gap="8px" width="100%">
            {paymentPlans.debit.map((pp) => (
              <Flex flexDirection="column" marginBottom="xl" marginRight="5em" marginLeft="l" key={pp.id}>
                <Text fontSize="s" textTransform="capitalize" color="neutral.600">{pp.card_brand}</Text>
                <Flex alignContent="center">
                  <Box marginTop="s" color="primary.800" marginRight="l"><FaCheck /></Box>
                  <Text>{planToString(pp)}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {paymentPlans.debitInternational.length > 0 && (
        <Box marginBottom="xl" width="100%">
          <Text marginY="xl" fontSize="xl" marginLeft="l">Débito internacional</Text>
          <Flex wrap="wrap" gap="8px" width="100%">
            {paymentPlans.debitInternational.map((pp) => (
              <Flex flexDirection="column" marginBottom="xl" marginRight="5em" marginLeft="l" key={pp.id}>
                <Text fontSize="s" textTransform="capitalize" color="neutral.600">{pp.card_brand}</Text>
                <Flex alignContent="center">
                  <Box marginTop="s" color="primary.800" marginRight="l"><FaCheck /></Box>
                  <Text>{planToString(pp)}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {paymentPlans.credit.length > 0 && (
        <Box marginBottom="xl" width="100%">
          <Text marginY="xl" fontSize="xl">Crédito</Text>
          <Flex wrap="wrap" gap="8px" width="100%">
            {paymentPlans.credit.map((pp) => (
              <Flex flexDirection="column" marginBottom="xl" marginRight="5em" marginLeft="l" key={pp.id}>
                <Text fontSize="s" textTransform="capitalize" color="neutral.600">{pp.card_brand}</Text>
                <Flex alignContent="center">
                  <Box marginTop="s" color="primary.800" marginRight="l"><FaCheck /></Box>
                  <Text>{planToString(pp)}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {paymentPlans.creditInternational.length > 0 && (
        <Box marginBottom="xl" width="100%">
          <Text marginY="xl" fontSize="xl" marginLeft="l">Crédito internacional</Text>
          <Flex wrap="wrap" gap="8px" width="100%">
            {paymentPlans.creditInternational.map((pp) => (
              <Flex flexDirection="column" marginBottom="xl" marginRight="5em" marginLeft="l" key={pp.id}>
                <Text fontSize="s" textTransform="capitalize" color="neutral.600">{pp.card_brand}</Text>
                <Flex alignContent="center">
                  <Box marginTop="s" color="primary.800" marginRight="l"><FaCheck /></Box>
                  <Text>{planToString(pp)}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {paymentPlans.creditSingleInstallment.length > 0 && (
        <Box marginBottom="xl" width="100%">
          <Text marginY="xl" fontSize="xl" marginLeft="l">Crédito (Cuota Única)</Text>
          <Flex wrap="wrap" gap="8px" width="100%">
            {paymentPlans.creditSingleInstallment.map((pp) => (
              <Flex flexDirection="column" marginBottom="xl" marginRight="5em" marginLeft="l" key={pp.id}>
                <Text fontSize="s" textTransform="capitalize" color="neutral.600">{pp.card_brand}</Text>
                <Flex alignContent="center">
                  <Box marginTop="s" color="primary.800" marginRight="l"><FaCheck /></Box>
                  <Text>{planToString(pp)}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {paymentPlans.creditSingleInstallmentInternational.length > 0 && (
        <Box marginBottom="xl" width="100%">
          <Text marginY="xl" fontSize="xl" marginLeft="l">Crédito Internacional (Cuota Única)</Text>
          <Flex wrap="wrap" gap="8px" width="100%">
            {paymentPlans.creditSingleInstallmentInternational.map((pp) => (
              <Flex flexDirection="column" marginBottom="xl" marginRight="5em" marginLeft="l" key={pp.id}>
                <Text fontSize="s" textTransform="capitalize" color="neutral.600">{pp.card_brand}</Text>
                <Flex alignContent="center">
                  <Box marginTop="s" color="primary.800" marginRight="l"><FaCheck /></Box>
                  <Text>{planToString(pp)}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {paymentPlans.creditMultipleInstallments.length > 0 && (
        <Box marginBottom="xl" width="100%">
          <Text marginY="xl" fontSize="xl" marginLeft="l">Crédito (Múltiples Cuotas)</Text>
          <Flex wrap="wrap" gap="8px" width="100%">
            {paymentPlans.creditMultipleInstallments.map((pp) => (
              <Flex flexDirection="column" marginBottom="xl" marginRight="5em" marginLeft="l" key={pp.id}>
                <Text fontSize="s" textTransform="capitalize" color="neutral.600">{pp.card_brand}</Text>
                <Flex alignContent="center">
                  <Box marginTop="s" color="primary.800" marginRight="l"><FaCheck /></Box>
                  <Text>{planToString(pp)}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {paymentPlans.creditMultipleInstallmentsInternational.length > 0 && (
        <Box marginBottom="xl" width="100%">
          <Text marginY="xl" fontSize="xl" marginLeft="l">Crédito Internacional (Múltiples Cuotas)</Text>
          <Flex wrap="wrap" gap="8px" width="100%">
            {paymentPlans.creditMultipleInstallmentsInternational.map((pp) => (
              <Flex flexDirection="column" marginBottom="xl" marginRight="5em" marginLeft="l" key={pp.id}>
                <Text fontSize="s" textTransform="capitalize" color="neutral.600">{pp.card_brand}</Text>
                <Flex alignContent="center">
                  <Box marginTop="s" color="primary.800" marginRight="l"><FaCheck /></Box>
                  <Text>{planToString(pp)}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {paymentPlans.prepaid.length > 0 && (
        <Box marginBottom="xl" width="100%">
          <Text marginY="xl" fontSize="xl">Prepago</Text>
          <Flex wrap="wrap" gap="8px" width="100%">
            {paymentPlans.prepaid.map((pp) => (
              <Flex flexDirection="column" marginBottom="xl" marginRight="xl" key={pp.id}>
                <Text fontSize="s" textTransform="capitalize" color="neutral.600">{pp.card_brand}</Text>
                <Flex alignContent="center">
                  <Box marginTop="s" color="primary.800" marginRight="l"><FaCheck /></Box>
                  <Text>{planToString(pp)}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {paymentPlans.prepaidInternational.length > 0 && (
        <Box marginBottom="xl" width="100%">
          <Text marginLeft="l" marginY="xl" fontSize="xl">Prepago internacional</Text>
          <Flex wrap="wrap" gap="8px" width="100%">
            {paymentPlans.prepaidInternational.map((pp) => (
              <Flex flexDirection="column" marginBottom="xl" marginRight="5em" marginLeft="l" key={pp.id}>
                <Text fontSize="s" textTransform="capitalize" color="neutral.600">{pp.card_brand}</Text>
                <Flex alignContent="center">
                  <Box marginTop="s" color="primary.800" marginRight="l"><FaCheck /></Box>
                  <Text>{planToString(pp)}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}
    </Flex>
  );
};
